import { Box, Stack, Text } from '@mantine/core'
import { useCallback, useRef, type RefObject } from 'react'
import {
  getBoggleIntervalRemainingMsFromTimeState,
  type BoggleStore,
} from '~/boggle/ui/state/Store'
import { useBoggleImperativeTimePaint } from '~/boggle/ui/run/useBoggleImperativeTimePaint'

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function resolveTimerGaugeProgressColor(
  progressColor: string | undefined,
  intervalCount: null | number,
  intervalIndex: null | number,
) {
  const isIntervalMode = intervalCount !== null && intervalCount > 1
  return (
    progressColor ??
    (isIntervalMode
      ? intervalIndex === intervalCount
        ? 'var(--mantine-color-red-4)'
        : '#f0c85a'
      : '#6dd1cc')
  )
}

function paintTimerGauge({
  intervalCount,
  intervalDurationMs,
  intervalIndex,
  intervalRemainingMs,
  progressColor,
  progressCircleRef,
  rootRef,
  secondsTextRef,
  strokeCircumference,
}: {
  intervalCount: null | number
  intervalDurationMs: number
  intervalIndex: null | number
  intervalRemainingMs: number
  progressColor?: string
  progressCircleRef: RefObject<SVGCircleElement | null>
  rootRef: RefObject<HTMLDivElement | null>
  secondsTextRef: RefObject<HTMLSpanElement | null>
  strokeCircumference: number
}) {
  const normalizedProgress =
    intervalDurationMs <= 0 ? 0 : clamp(intervalRemainingMs / intervalDurationMs, 0, 1)
  const dashOffset = strokeCircumference * (1 - normalizedProgress)
  const remainingSeconds = Math.ceil(intervalRemainingMs / 1000)
  const timeExceedsMaximum = remainingSeconds > intervalDurationMs / 1000
  const resolvedProgressColor = resolveTimerGaugeProgressColor(
    progressColor,
    intervalCount,
    intervalIndex,
  )
  const progressCircle = progressCircleRef.current

  if (progressCircle) {
    progressCircle.style.strokeDashoffset = String(dashOffset)
    progressCircle.style.stroke = timeExceedsMaximum
      ? 'var(--mantine-color-green-5)'
      : resolvedProgressColor
  }

  const secondsText = secondsTextRef.current
  if (secondsText) {
    secondsText.textContent = String(remainingSeconds)
  }

  const root = rootRef.current
  if (root) {
    const isIntervalMode = intervalCount !== null && intervalCount > 1
    root.setAttribute(
      'aria-label',
      isIntervalMode && intervalIndex !== null
        ? `${remainingSeconds} seconds remaining in interval ${intervalIndex} of ${intervalCount}`
        : `${remainingSeconds} seconds remaining`,
    )
  }
}

export function TimerGauge({
  diameter = 110,
  fontMetricsDiameter,
  intervalCount = null,
  intervalDurationMs,
  intervalIndex = null,
  intervalRemainingMs,
  marginTop = 6,
  progressColor,
  progressMeterWidth = 12,
  showUnitLabel = true,
  store,
}: {
  diameter?: number
  fontMetricsDiameter?: number
  intervalCount?: null | number
  intervalDurationMs: number
  intervalIndex?: null | number
  /** Static snapshot when `store` is omitted (e.g. end-buffer HUD). */
  intervalRemainingMs?: number
  marginTop?: number
  progressColor?: string
  progressMeterWidth?: number
  showUnitLabel?: boolean
  /** Live clock — arc and center text update imperatively on `subscribeTime`. */
  store?: BoggleStore
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const progressCircleRef = useRef<SVGCircleElement>(null)
  const secondsTextRef = useRef<HTMLSpanElement>(null)

  const padding = Math.max(12, progressMeterWidth / 2 + 6)
  const radius = diameter / 2 - padding
  const strokeCircumference = 2 * Math.PI * radius
  const center = diameter / 2
  const fontBasisDiameter = fontMetricsDiameter ?? diameter
  const valueFontSize = Math.max(16, Math.round(fontBasisDiameter * 0.25))
  const labelFontSize = Math.max(8, Math.round(fontBasisDiameter * 0.1))
  const isIntervalMode = intervalCount !== null && intervalCount > 1
  const staticRemainingMs = store == null ? (intervalRemainingMs ?? 0) : null
  const staticNormalizedProgress =
    staticRemainingMs !== null && intervalDurationMs > 0
      ? clamp(staticRemainingMs / intervalDurationMs, 0, 1)
      : null
  const staticDashOffset =
    staticNormalizedProgress !== null
      ? strokeCircumference * (1 - staticNormalizedProgress)
      : strokeCircumference
  const staticRemainingSeconds =
    staticRemainingMs !== null ? Math.ceil(staticRemainingMs / 1000) : 0
  const staticAriaLabel =
    staticRemainingMs !== null
      ? isIntervalMode && intervalIndex !== null
        ? `${staticRemainingSeconds} seconds remaining in interval ${intervalIndex} of ${intervalCount}`
        : `${staticRemainingSeconds} seconds remaining`
      : undefined

  const paint = useCallback(() => {
    const remainingMs =
      store != null
        ? getBoggleIntervalRemainingMsFromTimeState(store.getTimeState())
        : (intervalRemainingMs ?? 0)

    paintTimerGauge({
      intervalCount,
      intervalDurationMs,
      intervalIndex,
      intervalRemainingMs: remainingMs,
      progressColor,
      progressCircleRef,
      rootRef,
      secondsTextRef,
      strokeCircumference,
    })
  }, [
    intervalCount,
    intervalDurationMs,
    intervalIndex,
    intervalRemainingMs,
    progressColor,
    store,
    strokeCircumference,
  ])

  useBoggleImperativeTimePaint(
    paint,
    [intervalCount, intervalDurationMs, intervalIndex, intervalRemainingMs, progressColor, strokeCircumference],
    store ?? null,
  )

  return (
    <Stack
      ref={rootRef}
      align="center"
      aria-label={staticAriaLabel}
      h={diameter}
      justify="center"
      mt={marginTop}
      pos="relative"
      gap={0}
      role="img"
      w="100%"
    >
      <Box h={diameter} pos="relative" w={diameter}>
        <svg
          height="100%"
          style={{ display: 'block', transform: 'rotate(-90deg)' }}
          viewBox={`0 0 ${diameter} ${diameter}`}
          width="100%"
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            style={{
              fill: 'none',
              strokeWidth: progressMeterWidth,
              stroke: 'rgba(0, 0, 0, 0.7)',
            }}
          />
          <circle
            ref={progressCircleRef}
            cx={center}
            cy={center}
            r={radius}
            style={{
              fill: 'none',
              stroke: resolveTimerGaugeProgressColor(
                progressColor,
                intervalCount,
                intervalIndex,
              ),
              strokeDasharray: strokeCircumference,
              strokeDashoffset: staticDashOffset,
              strokeLinecap: 'round',
              strokeWidth: progressMeterWidth,
            }}
          />
        </svg>
        <Stack
          align="center"
          bottom={0}
          gap={0}
          justify="center"
          left={0}
          pos="absolute"
          right={0}
          top={0}
        >
          <Text c={'#f2e8d9'} fz={valueFontSize} fw={800} lh={1}>
            <span ref={secondsTextRef}>{staticRemainingSeconds}</span>
          </Text>
          {!isIntervalMode && showUnitLabel ? (
            <Text
              c="rgba(242, 232, 217, 0.62)"
              fz={labelFontSize}
              lts="0.12em"
              mt={2}
              tt="uppercase"
            >
              sec
            </Text>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  )
}
